import React, { useRef, useEffect, useState, Fragment, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { createPortal } from 'react-dom';
import type { RootState } from '../../store';
import type { LiveFinding, LiveMessage, TimedSegment } from '../../store/slices/liveSessionSlice';
import clsx from 'clsx';

// ─── Tajweed Rule Reference Data ─────────────────────────────────
interface ExampleAyah {
  surah: number;
  ayah: number;
  wordAr: string;
  wordEn: string;
}

interface RuleInfo {
  id: string;
  nameAr: string;
  nameEn: string;
  descriptionEn: string;
  descriptionAr: string;
  examples: ExampleAyah[];
  color: string;
}

const RULE_DB: RuleInfo[] = [
  {
    id: 'madd',
    nameAr: 'مدّ',
    nameEn: 'Madd',
    descriptionEn: 'Stretch the vowel sound. Natural madd (tabee\'i) is 2 beats. Connected madd can be 4-6 beats.',
    descriptionAr: 'إطالة الصوت بحرف المدّ. المدّ الطبيعي مقدار حركتين، والمدّ المتصل ٤-٦ حركات.',
    examples: [
      { surah: 1, ayah: 3, wordAr: 'الرَّحِيمِ', wordEn: 'ar-Raheem' },
      { surah: 1, ayah: 1, wordAr: 'الرَّحْمَٰنِ', wordEn: 'ar-Rahman' },
      { surah: 112, ayah: 2, wordAr: 'الصَّمَدُ', wordEn: 'as-Samad' },
    ],
    color: 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-500/10',
  },
  {
    id: 'ghunnah',
    nameAr: 'غنّة',
    nameEn: 'Ghunnah',
    descriptionEn: 'A nasal sound from the nose lasting 2 beats. Occurs on noon or meem with shaddah.',
    descriptionAr: 'صوت أنفي يخرج من الخيشوم مقدار حركتين. يكون عند النون أو الميم المشدّدة.',
    examples: [
      { surah: 2, ayah: 6, wordAr: 'إِنَّ', wordEn: 'inna' },
      { surah: 1, ayah: 5, wordAr: 'إِيَّاكَ', wordEn: 'iyyaka' },
      { surah: 114, ayah: 1, wordAr: 'النَّاسِ', wordEn: 'an-Naas' },
    ],
    color: 'text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-500/10',
  },
  {
    id: 'idgham',
    nameAr: 'إدغام',
    nameEn: 'Idgham',
    descriptionEn: 'Merge noon sakinah into the following letter. With ghunnah (ya/noon/meem/waw) or without (lam/ra).',
    descriptionAr: 'إدخال النون الساكنة في الحرف الذي يليها. مع غنّة (ينمو) أو بدون غنّة (لر).',
    examples: [
      { surah: 112, ayah: 1, wordAr: 'أَحَدٌ', wordEn: 'Ahad (into Allahu)' },
      { surah: 2, ayah: 27, wordAr: 'مِن بَعْدِ', wordEn: 'min ba\'di' },
      { surah: 36, ayah: 1, wordAr: 'يس', wordEn: 'Ya-Seen' },
    ],
    color: 'text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-500/10',
  },
  {
    id: 'ikhfa',
    nameAr: 'إخفاء',
    nameEn: 'Ikhfa',
    descriptionEn: 'Conceal the noon sakinah with a light nasal for 2 beats. Occurs before 15 specific letters.',
    descriptionAr: 'إخفاء النون الساكنة مع غنّة مقدار حركتين. يكون قبل ١٥ حرفًا محددًا.',
    examples: [
      { surah: 2, ayah: 10, wordAr: 'أَنفُسَهُم', wordEn: 'anfusahum' },
      { surah: 1, ayah: 7, wordAr: 'أَنْعَمْتَ', wordEn: 'an\'amta' },
      { surah: 3, ayah: 26, wordAr: 'مِن تَشَاءُ', wordEn: 'min tasha' },
    ],
    color: 'text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-500/10',
  },
  {
    id: 'iqlab',
    nameAr: 'إقلاب',
    nameEn: 'Iqlab',
    descriptionEn: 'Convert noon sakinah into a meem sound when followed by the letter ba (ب).',
    descriptionAr: 'قلب النون الساكنة ميمًا مخفاة عند ملاقاة حرف الباء.',
    examples: [
      { surah: 2, ayah: 4, wordAr: 'مِنۢ بَعْدِ', wordEn: 'min ba\'di' },
      { surah: 6, ayah: 94, wordAr: 'أَنۢبِيَاءَ', wordEn: 'anbiya' },
    ],
    color: 'text-teal-600 dark:text-teal-400 bg-teal-50 dark:bg-teal-500/10',
  },
  {
    id: 'qalqalah',
    nameAr: 'قلقلة',
    nameEn: 'Qalqalah',
    descriptionEn: 'A bouncing/echoing sound on the letters ق ط ب ج د when they have sukoon. Stronger at the end of an ayah.',
    descriptionAr: 'اضطراب الصوت عند النطق بأحرف (قطب جد) الساكنة. تكون أقوى في الوقف.',
    examples: [
      { surah: 112, ayah: 4, wordAr: 'أَحَدٌ', wordEn: 'Ahad' },
      { surah: 113, ayah: 1, wordAr: 'الْفَلَقِ', wordEn: 'al-Falaq' },
      { surah: 85, ayah: 1, wordAr: 'الْبُرُوجِ', wordEn: 'al-Burooj' },
    ],
    color: 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-500/10',
  },
];

// Build patterns for matching terms in text
const TERM_PATTERNS: { pattern: RegExp; rule: RuleInfo }[] = [];
for (const rule of RULE_DB) {
  const esc = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const pats = [rule.nameAr, rule.id, rule.nameEn];
  TERM_PATTERNS.push({
    pattern: new RegExp(`(${pats.map(esc).join('|')})`, 'gi'),
    rule,
  });
}

function buildAudioUrl(surah: number, ayah: number): string {
  const s = String(surah).padStart(3, '0');
  const a = String(ayah).padStart(3, '0');
  return `https://everyayah.com/data/Husary_128kbps/${s}${a}.mp3`;
}

function getSessionLang(): string {
  return localStorage.getItem('tajweed_session_lang') || 'en';
}

// ─── Tajweed Tooltip (rendered via portal to avoid clipping) ─────
function RuleTooltip({ rule, anchor, onClose }: { rule: RuleInfo; anchor: DOMRect; onClose: () => void }) {
  const lang = getSessionLang();
  const isAr = lang === 'ar';
  const [playing, setPlaying] = useState<number | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const togglePlay = (idx: number, surah: number, ayah: number) => {
    if (!audioRef.current) audioRef.current = new Audio();
    const a = audioRef.current;
    if (playing === idx) { a.pause(); setPlaying(null); return; }
    a.src = buildAudioUrl(surah, ayah);
    a.onended = () => setPlaying(null);
    a.onerror = () => setPlaying(null);
    a.play().then(() => setPlaying(idx)).catch(() => setPlaying(null));
  };

  useEffect(() => { return () => { audioRef.current?.pause(); }; }, []);

  // Position: below the anchor, centered horizontally, clamped to viewport
  const top = anchor.bottom + 6;
  const left = Math.max(8, Math.min(anchor.left + anchor.width / 2 - 140, window.innerWidth - 288));

  return createPortal(
    <>
      <div className="fixed inset-0 z-[200]" onClick={onClose} />
      <div
        className="fixed z-[201] w-72 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl"
        style={{ top, left }}
        dir={isAr ? 'rtl' : 'ltr'}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-3 pb-2">
          <div>
            <span className="text-sm font-semibold text-gray-900 dark:text-white">{isAr ? rule.nameAr : rule.nameEn}</span>
            <span className="text-sm text-gray-400 mx-1.5">{isAr ? rule.nameEn : rule.nameAr}</span>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 shrink-0">
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Description */}
        <p className="text-xs text-gray-600 dark:text-gray-400 px-3 pb-2">
          {isAr ? rule.descriptionAr : rule.descriptionEn}
        </p>

        {/* Examples */}
        <div className="border-t border-gray-100 dark:border-gray-700 px-3 py-2">
          <p className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1.5">
            {isAr ? 'أمثلة' : 'Examples'}
          </p>
          <div className="space-y-1.5">
            {rule.examples.map((ex, idx) => (
              <div key={idx} className="flex items-center gap-2 p-1.5 bg-gray-50 dark:bg-gray-900 rounded-lg">
                <button
                  onClick={() => togglePlay(idx, ex.surah, ex.ayah)}
                  className={clsx(
                    'w-6 h-6 rounded-full flex items-center justify-center shrink-0 transition-all',
                    playing === idx ? 'bg-amber-500 text-white' : 'bg-emerald-600 hover:bg-emerald-500 text-white'
                  )}
                >
                  {playing === idx ? (
                    <svg className="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 24 24"><rect x="6" y="5" width="4" height="14" rx="1" /><rect x="14" y="5" width="4" height="14" rx="1" /></svg>
                  ) : (
                    <svg className="w-2.5 h-2.5 ml-0.5" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
                  )}
                </button>
                <div className="min-w-0 flex-1">
                  <p className="text-[11px] text-gray-700 dark:text-gray-300 font-arabic">{ex.wordAr}</p>
                  <p className="text-[10px] text-gray-400 dark:text-gray-500">{ex.surah}:{ex.ayah} — {ex.wordEn}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>,
    document.body
  );
}

// ─── Interactive Text ────────────────────────────────────────────
function InteractiveText({ text }: { text: string }) {
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);
  const [activeRule, setActiveRule] = useState<RuleInfo | null>(null);

  const handleClick = useCallback((key: string, rule: RuleInfo, el: HTMLElement) => {
    if (activeKey === key) {
      setActiveKey(null);
      setActiveRule(null);
      setAnchorRect(null);
    } else {
      setActiveKey(key);
      setActiveRule(rule);
      setAnchorRect(el.getBoundingClientRect());
    }
  }, [activeKey]);

  type Seg = { type: 'text'; value: string } | { type: 'term'; value: string; rule: RuleInfo };

  interface Match { start: number; end: number; text: string; rule: RuleInfo }
  const allMatches: Match[] = [];
  for (const { pattern, rule } of TERM_PATTERNS) {
    const p = new RegExp(pattern.source, pattern.flags);
    let m: RegExpExecArray | null;
    while ((m = p.exec(text)) !== null) {
      if (m[0].length < 2) continue;
      allMatches.push({ start: m.index, end: m.index + m[0].length, text: m[0], rule });
    }
  }
  allMatches.sort((a, b) => a.start - b.start);
  const filtered: Match[] = [];
  let lastEnd = 0;
  for (const match of allMatches) {
    if (match.start >= lastEnd) { filtered.push(match); lastEnd = match.end; }
  }

  const segments: Seg[] = [];
  let cursor = 0;
  for (const match of filtered) {
    if (match.start > cursor) segments.push({ type: 'text', value: text.slice(cursor, match.start) });
    segments.push({ type: 'term', value: match.text, rule: match.rule });
    cursor = match.end;
  }
  if (cursor < text.length) segments.push({ type: 'text', value: text.slice(cursor) });

  if (filtered.length === 0) return <p>{text}</p>;

  return (
    <p>
      {segments.map((seg, i) => {
        if (seg.type === 'text') return <Fragment key={i}>{seg.value}</Fragment>;
        const key = `${seg.rule.id}-${i}`;
        return (
          <span
            key={i}
            onClick={(e) => handleClick(key, seg.rule, e.currentTarget as HTMLElement)}
            className={clsx(
              'inline rounded px-0.5 -mx-0.5 cursor-pointer transition-colors font-medium underline decoration-dotted underline-offset-2',
              seg.rule.color
            )}
          >
            {seg.value}
          </span>
        );
      })}
      {activeRule && anchorRect && (
        <RuleTooltip rule={activeRule} anchor={anchorRect} onClose={() => { setActiveKey(null); setActiveRule(null); setAnchorRect(null); }} />
      )}
    </p>
  );
}

// ─── Findings Card ───────────────────────────────────────────────
const SEVERITY_COLORS: Record<string, string> = {
  critical: 'border-red-400/40 bg-red-50 dark:bg-red-500/10',
  major: 'border-orange-400/40 bg-orange-50 dark:bg-orange-500/10',
  minor: 'border-yellow-400/40 bg-yellow-50 dark:bg-yellow-500/10',
};

function FindingCard({ finding }: { finding: LiveFinding }) {
  const colors = SEVERITY_COLORS[finding.severity] || SEVERITY_COLORS.minor;
  return (
    <div className={clsx('rounded-lg border p-2.5 text-xs', colors)}>
      <div className="flex items-center gap-2 mb-1">
        <span className="font-semibold text-gray-700 dark:text-gray-200 capitalize">{finding.rule.replace(/_/g, ' ')}</span>
        <span className={clsx(
          'px-1.5 py-0.5 rounded text-[10px] font-medium',
          finding.severity === 'critical' ? 'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300' :
          finding.severity === 'major' ? 'bg-orange-100 text-orange-700 dark:bg-orange-500/20 dark:text-orange-300' :
          'bg-yellow-100 text-yellow-700 dark:bg-yellow-500/20 dark:text-yellow-300'
        )}>{finding.severity}</span>
      </div>
      <p className="text-gray-600 dark:text-gray-300">{finding.explanation_for_user}</p>
      {finding.repair_hint && <p className="text-emerald-600 dark:text-emerald-400 mt-1">Fix: {finding.repair_hint}</p>}
      {finding.evidence && (finding.evidence.expected || finding.evidence.observed) && (
        <div className="flex gap-3 mt-1 text-gray-500 dark:text-gray-400">
          {finding.evidence.expected && <span>Expected: {finding.evidence.expected}</span>}
          {finding.evidence.observed && <span>Observed: {finding.evidence.observed}</span>}
        </div>
      )}
    </div>
  );
}

// ─── Replay with Karaoke Highlighting ────────────────────────────
function ReplayButton({
  audioUrl,
  timedSegments,
  onTimeUpdate,
}: {
  audioUrl: string;
  timedSegments?: TimedSegment[];
  onTimeUpdate?: (timeMs: number | null) => void;
}) {
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const animRef = useRef<number>(0);

  const stopTracking = () => {
    cancelAnimationFrame(animRef.current);
    onTimeUpdate?.(null);
  };

  const trackTime = () => {
    if (audioRef.current && !audioRef.current.paused) {
      onTimeUpdate?.(audioRef.current.currentTime * 1000);
      animRef.current = requestAnimationFrame(trackTime);
    }
  };

  const toggle = () => {
    if (!audioRef.current) audioRef.current = new Audio();
    const a = audioRef.current;
    if (playing) {
      a.pause();
      setPlaying(false);
      stopTracking();
      return;
    }
    a.src = audioUrl;
    a.onended = () => { setPlaying(false); stopTracking(); };
    a.onerror = () => { setPlaying(false); stopTracking(); };
    a.play().then(() => {
      setPlaying(true);
      if (timedSegments?.length) animRef.current = requestAnimationFrame(trackTime);
    }).catch(() => setPlaying(false));
  };

  useEffect(() => { return () => { audioRef.current?.pause(); stopTracking(); }; }, []);

  return (
    <button
      onClick={toggle}
      className={clsx(
        'mt-1.5 flex items-center gap-1 text-[11px] transition-colors',
        playing ? 'text-amber-500' : 'text-gray-400 dark:text-gray-500 hover:text-emerald-500 dark:hover:text-emerald-400'
      )}
    >
      {playing ? (
        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24"><rect x="6" y="5" width="4" height="14" rx="1" /><rect x="14" y="5" width="4" height="14" rx="1" /></svg>
      ) : (
        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19.114 5.636a9 9 0 010 12.728M16.463 8.288a5.25 5.25 0 010 7.424M6.75 8.25l4.72-4.72a.75.75 0 011.28.53v15.88a.75.75 0 01-1.28.53l-4.72-4.72H4.51c-.88 0-1.704-.507-1.938-1.354A9.01 9.01 0 012.25 12c0-.83.112-1.633.322-2.396C2.806 8.756 3.63 8.25 4.51 8.25H6.75z" />
        </svg>
      )}
      {playing ? 'Playing...' : 'Replay'}
    </button>
  );
}

// ─── Karaoke Text (highlights segments as audio plays) ───────────
function KaraokeTeacherText({
  text,
  timedSegments,
  currentTimeMs,
}: {
  text: string;
  timedSegments: TimedSegment[];
  currentTimeMs: number | null;
}) {
  // Map timed segments back to positions in the display text
  // Display text has markers stripped — we need to find each segment's text in it
  return (
    <p>
      {timedSegments.map((seg, i) => {
        const isActive = currentTimeMs != null &&
          currentTimeMs >= seg.start_ms &&
          currentTimeMs < seg.end_ms;
        const isPast = currentTimeMs != null && currentTimeMs >= seg.end_ms;

        return (
          <Fragment key={i}>
            {i > 0 && ' '}
            <span
              className={clsx(
                'transition-colors duration-150',
                isActive && 'bg-yellow-200 dark:bg-yellow-500/30 text-yellow-900 dark:text-yellow-100 rounded',
                isPast && 'text-gray-400 dark:text-gray-500',
              )}
            >
              {seg.text}
            </span>
          </Fragment>
        );
      })}
    </p>
  );
}

// ─── Single Message Component ────────────────────────────────────
function TeacherMessage({ msg, messageIndex }: { msg: LiveMessage; messageIndex: number }) {
  const [replayTime, setReplayTime] = useState<number | null>(null);
  const hasTimedSegments = msg.timedSegments && msg.timedSegments.length > 0;

  // Read global playback state (from initial auto-play via audio queue)
  const playingIdx = useSelector((state: RootState) => state.liveSession.playingMessageIndex);
  const globalTime = useSelector((state: RootState) => state.liveSession.playbackTimeMs);

  // Use global time if this message is the one currently auto-playing, otherwise use replay time
  const isAutoPlaying = playingIdx === messageIndex;
  const currentTime = isAutoPlaying ? globalTime : replayTime;

  return (
    <div className="max-w-[85%] rounded-2xl px-4 py-3 text-sm bg-emerald-50 dark:bg-emerald-500/10 text-gray-700 dark:text-gray-200 rounded-tl-sm">
      <p className="text-xs font-semibold text-emerald-600 dark:text-emerald-400 mb-1">Ustadh Noor</p>

      {/* Karaoke text when playing (auto or replay), interactive text otherwise */}
      {hasTimedSegments && currentTime != null ? (
        <KaraokeTeacherText
          text={msg.text}
          timedSegments={msg.timedSegments!}
          currentTimeMs={currentTime}
        />
      ) : (
        <InteractiveText text={msg.text} />
      )}

      {msg.score != null && (
        <p className={clsx(
          'mt-2 text-xs font-semibold',
          msg.score >= 80 ? 'text-emerald-600 dark:text-emerald-400' :
          msg.score >= 60 ? 'text-yellow-600 dark:text-yellow-400' :
          'text-red-500 dark:text-red-400'
        )}>Score: {Math.round(msg.score)}/100</p>
      )}
      {msg.findings && msg.findings.length > 0 && (
        <div className="mt-2 space-y-1.5">
          {msg.findings.map((f, fi) => <FindingCard key={fi} finding={f} />)}
        </div>
      )}
      {msg.findings && msg.findings.length === 0 && msg.score != null && msg.score >= 80 && (
        <p className="mt-1 text-xs text-emerald-600 dark:text-emerald-400">No errors detected</p>
      )}
      {msg.audioUrl && (
        <ReplayButton
          audioUrl={msg.audioUrl}
          timedSegments={msg.timedSegments}
          onTimeUpdate={hasTimedSegments ? setReplayTime : undefined}
        />
      )}
    </div>
  );
}

// ─── Conversation Log ────────────────────────────────────────────
export default function ConversationLog() {
  const messages = useSelector((state: RootState) => state.liveSession.messages);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length]);

  if (messages.length === 0) return null;

  return (
    <div ref={containerRef} className="flex flex-col gap-3 max-h-[28rem] overflow-y-auto px-1">
      {messages.map((msg, idx) => (
        <div key={idx} className={clsx('flex', msg.role === 'teacher' ? 'justify-start' : 'justify-end')}>
          {msg.role === 'teacher' ? (
            <TeacherMessage msg={msg} messageIndex={idx} />
          ) : (
            <div className="max-w-[85%] rounded-2xl px-4 py-3 text-sm bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 rounded-tr-sm">
              <p>{msg.text}</p>
              {msg.score != null && (
                <p className={clsx(
                  'mt-2 text-xs font-semibold',
                  msg.score >= 80 ? 'text-emerald-600 dark:text-emerald-400' :
                  msg.score >= 60 ? 'text-yellow-600 dark:text-yellow-400' :
                  'text-red-500 dark:text-red-400'
                )}>Score: {Math.round(msg.score)}/100</p>
              )}
              {msg.findings && msg.findings.length > 0 && (
                <div className="mt-2 space-y-1.5">
                  {msg.findings.map((f, fi) => <FindingCard key={fi} finding={f} />)}
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
